import { formatApiMessage } from "./formatApiMessage";

function mapPlacementRow(row) {
  const st = String(row.status ?? "").trim().toUpperCase();
  const failed =
    st === "REJECTED" ||
    st === "FAILED" ||
    st === "FAILURE" ||
    st === "ORDER NOT PLACED";
  const completed = !failed && (st === "SUCCESS" || st === "COMPLETED");
  const statusOut = completed ? "COMPLETED" : row.status != null && row.status !== "" ? String(row.status) : "-";
  return {
    broker_order_id: row.broker_order_id,
    exchange_trading_symbol: row.exchange_trading_symbol ?? row.trading_symbol ?? "-",
    broker_client_code: row.broker_client_code ?? "-",
    order_type: row.order_type ?? "-",
    order_quantity: row.order_quantity ?? "-",
    order_price: row.order_price ?? "-",
    transaction_type: row.transaction_type ?? "-",
    status: statusOut,
    message: row.message,
    remarks: row.remarks ?? row.remark ?? row.Remarks,
    reason: row.reason,
  };
}

function normalizeErrors(errList) {
  if (!Array.isArray(errList)) return [];
  return errList.map((err) => {
    if (typeof err === "string") {
      return { error: err, square_off_order: {} };
    }
    const so =
      err.square_off_order ?? {
        exchange_trading_symbol: err.exchange_trading_symbol ?? err.trading_symbol,
        broker_client_code: err.broker_client_code,
        order_type: err.order_type,
        order_quantity: err.order_quantity,
        order_price: err.order_price,
      };
    return {
      error:
        formatApiMessage(err.error) ??
        formatApiMessage(err.message) ??
        "Unknown error",
      remarks: err.remarks ?? err.remark ?? so.remarks ?? so.remark,
      square_off_order: so,
    };
  });
}

/**
 * Maps broker-gateway responses (square off, modify, repeat, bulk place, order initiate, etc.)
 * to the shape expected by OrderStatusPage (`placement_response` + `errors`).
 */
export function normalizeGatewayResponseToOrderStatus(data) {
  if (!data || typeof data !== "object") {
    return { placement_response: [], errors: [] };
  }

  const errors = normalizeErrors(data.errors);

  let rawRows = null;
  if (Array.isArray(data.placement_response) && data.placement_response.length > 0) {
    rawRows = data.placement_response;
  } else if (Array.isArray(data.modification_response) && data.modification_response.length > 0) {
    rawRows = data.modification_response;
  } else if (Array.isArray(data.repeat_response) && data.repeat_response.length > 0) {
    rawRows = data.repeat_response;
  } else if (Array.isArray(data.cancellation_response) && data.cancellation_response.length > 0) {
    rawRows = data.cancellation_response;
  }

  if (rawRows) {
    return {
      placement_response: rawRows.map(mapPlacementRow),
      errors,
    };
  }

  if (data.status === true && (data.message != null || data.modified_count != null)) {
    return {
      placement_response: [
        {
          exchange_trading_symbol: "-",
          broker_client_code: "-",
          order_type: "-",
          order_quantity: data.modified_count != null ? String(data.modified_count) : "-",
          order_price: "-",
          transaction_type: "-",
          status: "COMPLETED",
          message: data.message ?? "",
        },
      ],
      errors,
    };
  }

  return { placement_response: [], errors: [] };
}

/** Router state for the order summary page after a broker action. */
export function orderStatusNavigationStateFromResult(result) {
  return { squareOffResponse: normalizeGatewayResponseToOrderStatus(result) };
}

const ORDER_STATUS_STORAGE_PREFIX = "orderStatusPayload:";
const ORDER_STATUS_QUERY_PARAM = "os";
const ORDER_STATUS_MAX_STORED = 20;

function pruneStoredOrderStatusPayloads() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(ORDER_STATUS_STORAGE_PREFIX)) keys.push(key);
    }
    if (keys.length <= ORDER_STATUS_MAX_STORED) return;
    const ranked = keys
      .map((key) => {
        try {
          const parsed = JSON.parse(localStorage.getItem(key) || "{}");
          return { key, ts: Number(parsed?.ts) || 0 };
        } catch {
          return { key, ts: 0 };
        }
      })
      .sort((a, b) => b.ts - a.ts);
    ranked.slice(ORDER_STATUS_MAX_STORED).forEach(({ key }) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

export function readOrderStatusPageState(id) {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(ORDER_STATUS_STORAGE_PREFIX + id);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Opens Order Status in a new tab so the current book/initiate page stays put. */
export function openOrderStatusInNewTab(state) {
  const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    localStorage.setItem(
      ORDER_STATUS_STORAGE_PREFIX + id,
      JSON.stringify({ ...state, ts: Date.now() })
    );
    pruneStoredOrderStatusPayloads();
  } catch {
    /* quota — still try to open the page */
  }
  const url = `/order-status?${ORDER_STATUS_QUERY_PARAM}=${encodeURIComponent(id)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

export const ORDER_STATUS_RESULT_QUERY = ORDER_STATUS_QUERY_PARAM;

